/**
 * Converts an image file to WebP format with resizing.
 * Modern browsers automatically apply EXIF orientation when loading images,
 * so drawing to canvas "bakes in" the correct orientation without manual rotation.
 */
export const fileConverter = async (file: File): Promise<File | null> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const imageUrl = URL.createObjectURL(file);
    img.src = imageUrl;

    img.onload = () => {
      URL.revokeObjectURL(imageUrl);

      const MAX_WIDTH = 1920;
      const MAX_HEIGHT = 1080;
      let width = img.width;
      let height = img.height;

      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        const widthRatio = MAX_WIDTH / width;
        const heightRatio = MAX_HEIGHT / height;
        const minRatio = Math.min(widthRatio, heightRatio);

        width = Math.round(width * minRatio);
        height = Math.round(height * minRatio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);

      canvas.toBlob((blob) => {
        if (blob) {
          // Add timestamp to filename to bust CDN cache on re-uploads
          const baseName = file.name.replace(/\.[^.]+$/, "");
          const timestamp = Date.now();
          const webpFile = new File([blob], `${baseName}_${timestamp}.webp`, { type: 'image/webp' });
          resolve(webpFile);
        } else {
          reject(new Error("Conversion to WebP failed"));
        }
      }, 'image/webp');
    };

    img.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error("Failed to load the image"));
    };
  });
};